import { Component, inject, ViewChild, effect } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { RefParamsService } from '../ref-params/ref-params.service';
import { PassingMode, PassingModeDto, passingModeStep, passingModeType, RefTypeEnum } from '@/types';
import { PassingModeService } from './passing-mode.service';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DataTableRowActions } from '@/components/data-table-builder/data-table.types';
import { getpassingModeDataTableStructure } from './utils/passing-mode-data-table-object';
import { PassingModeRepository } from '@/store/passing-mode-state/passing-mode-state.repository';
import { getPassingModeAddDrawerObject } from './utils/passing-mode-create-drawer-object';
import { getPassingModeFormObject } from './utils/passing-mode-form-object';
import { getPassingModeDeleteObject } from './utils/passing-mode-delete-dialog-object';
import { getPassingModeUpdateDrawerObject } from './utils/passing-mode-update-drawer-object';
import { createServerQuery } from '@/components/data-table-builder/server-query';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { passingModeSchema } from '@/validations/passing-mode.validation';
import { passingModeStepLabels, passingModeTypeLabels } from '@/pages/settings/passing-mode/utils/type-labels';

@Component({
  selector: 'sm-passing-mode',
  imports: [PageHeaderComponent, DataTableBuilderComponent, DrawerComponent, DialogComponent],
  templateUrl: './passing-mode.component.html',
  styleUrl: './passing-mode.component.scss',
})
export class PassingModeComponent {
  translate = inject(TranslateService);
  messageService = inject(MessageService);
  refParamsService = inject(RefParamsService);
  passingModeService = inject(PassingModeService);
  passingModeRepository = inject(PassingModeRepository);

  breadcrumb = [
    {
      label: 'settings.passingMode.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.passingMode.breadcrumb.passingMode',
      routerLink: ['/settings', 'passing-mode'],
    },
  ];

  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  passingModeDrawer?: DrawerObject;

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  passingModeDialog?: DialogObject;

  marketTypesOptions: SelectOption[] = [];
  formObject = getFormBuilderObjectFactory();

  rowActions: DataTableRowActions = {
    editAction: (row: PassingMode) => {
      this.passingModeRepository.setNested('response', 'id', row.id);
      this.passingModeRepository.set('dto', {
        libelle: row.libelle,
        restraint: row.restraint,
        type: row.type as passingModeType,
        etapes: row.etapes as passingModeStep,
        typeMarchersIds: row.typeMarchers?.map(tm => tm.id) ?? [],
      });

      this.passingModeRepository.set('typeMarchersSelected', row.typeMarchers?.map(tm => ({ name: tm.description, code: tm.id })) ?? []);
      this.passingModeRepository.set('passingModeTypeSelected', { name: passingModeTypeLabels[row.type as passingModeType], code: row.type });
      this.passingModeRepository.set('passingModeStepSelected', { name: passingModeStepLabels[row.etapes as passingModeStep], code: row.etapes });

      this.formObject = getPassingModeFormObject({ store: this.passingModeRepository, typeMarchersOptions: this.marketTypesOptions });
      this.passingModeDrawer = getPassingModeUpdateDrawerObject({
        store: this.passingModeRepository,
        formObject: this.formObject,
        action: () => this.editPassingMode(),
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
      });
      this.drawerComponent.toggleVisible();
    },
    deleteAction: (row: PassingMode) => {
      this.passingModeDialog = getPassingModeDeleteObject({
        store: this.passingModeRepository,
        action: () => {
          this.deletePassingMode(row.id);
          this.dialogComponent.toggleVisible();
        },
        toggleVisiblity: () => this.dialogComponent.toggleVisible(),
      });
      this.dialogComponent.toggleVisible();
    },
  };

  loading = true;
  data: PassingMode[] = [];

  // Server pagination
  serverQuery = createServerQuery({});

  dataTableObject = getpassingModeDataTableStructure({
    store: this.passingModeRepository,
    createAction: () => {
      this.passingModeDrawer = getPassingModeAddDrawerObject({
        store: this.passingModeRepository,
        formObject: this.formObject,
        toggleVisibility: () => this.drawerComponent.toggleVisible(),
        action: () => this.addPassingMode(),
      });
      this.drawerComponent.toggleVisible();
    },
    rowActions: this.rowActions,
    serverQuery: this.serverQuery,
    enableServerActions: true,
    marketTypesOptions: this.marketTypesOptions,
  });

  totalRecords = 0;

  loadData() {
    const page = this.serverQuery.page();
    const size = this.serverQuery.pageSize();
    const search = this.serverQuery.search();
    const sortBy = this.serverQuery.sortBy();
    const sortOrder = this.serverQuery.sortOrder();
    const sort = sortBy ? `${sortBy},${sortOrder}` : '';
    this.passingModeService.getAll({ page, size, search, sort }).subscribe({
      next: data => {
        this.totalRecords = (data as any).totalElements ?? data.content.length;
        this.data = (data as any).content ?? [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.passingMode.message.error.loading'),
        });
      },
    });

    this.refParamsService.getSpecializedRefParams(RefTypeEnum.MARKET_TYPE).subscribe({
      next: data => {
        this.marketTypesOptions = data.map(ref => ({ name: ref.description, code: ref.id }));
        this.formObject = getPassingModeFormObject({
          store: this.passingModeRepository,
          typeMarchersOptions: this.marketTypesOptions,
        });
      },
    });
  }

  constructor() {
    effect(() => {
      this.loadData();
    });
  }

  addPassingMode() {
    const payload = this.passingModeRepository.get<PassingModeDto>('dto');
    const result = passingModeSchema.safeParse(payload);
    if (result.success) {
      this.passingModeService.add(payload).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData();
          this.passingModeRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.passingMode.message.success.added'),
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.passingMode.message.error.added'),
          });
        },
      });
    } else {
      this.passingModeRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  editPassingMode() {
    const id = this.passingModeRepository.getNested<number>('response', 'id');
    const payload = this.passingModeRepository.get<PassingModeDto>('dto');
    const result = passingModeSchema.safeParse(payload);
    if (result.success) {
      this.passingModeService.update(id, payload).subscribe({
        next: () => {
          this.drawerComponent.toggleVisible();
          this.loadData();
          this.passingModeRepository.reset();
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('settings.passingMode.message.success.edited'),
          });
          this.passingModeRepository.reset();
        },
        error: err => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('settings.passingMode.message.error.edited'),
          });
        },
      });
    } else {
      this.passingModeRepository.setErrors(result.error.flatten().fieldErrors as Record<string, string[]>);
    }
  }

  deletePassingMode(id: number) {
    this.passingModeService.delete(id).subscribe({
      next: () => {
        this.loadData();
        this.passingModeRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.passingMode.message.success.deleted'),
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.passingMode.message.error.deleted'),
        });
      },
    });
  }
}
